import { useState } from "react";
import {
  LayoutDashboard, Sparkles, LineChart, Zap, Rocket, Dna, Telescope, Mail, Plug, Users,
  Grid3x3, Clock, Flame, UserMinus, ShieldAlert, Search, Link2, Compass, Heart, MousePointerClick,
  BookOpen, TrendingUp, AlertTriangle, Play,
} from "lucide-react";
import { restartTour } from "../components/ProductTour";

const SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: BookOpen,
    intro: "Three-minute tour. Connect Facebook → set up Team keys → arm the Auto-Operator → you're running.",
    features: [
      {
        id: "onboard",
        title: "First-run onboarding",
        why: "Nothing works until your Workspace has S1 + Resend credentials and at least one FB ad account is connected.",
        how: [
          "Admin: open Team & Keys → paste System1 API key + Resend API key.",
          "Each buyer: open My Facebook → connect your personal FB token (never shared across the team).",
          "Verify your vertical map: Dashboard → filter by vertical to confirm campaigns categorize correctly.",
        ],
      },
    ],
  },

  {
    id: "dashboard",
    title: "Performance Dashboard",
    icon: LayoutDashboard,
    intro: "Single-pane-of-glass for spend, revenue, net profit and ROI across every connected ad account + System1 account.",
    features: [
      {
        id: "kpis",
        title: "KPI bar",
        why: "Your whole P&L in under 2 seconds. Profit and ROI are the only two numbers that matter — they always live at the top.",
        how: [
          "Toggle date range: Today / Yesterday / 7d / 30d.",
          "Filter by vertical or by individual buyer (admin only) to isolate a team member's performance.",
        ],
      },
      {
        id: "table",
        title: "Campaigns table",
        why: "One row per FB campaign joined against System1 revenue. Sort by profit, ROI, or CTR to triage in seconds.",
        how: [
          "Click the status badge to pause / unpause directly (no trip to Ads Manager).",
          "Use the sync button if you pushed changes in Meta and want fresh numbers immediately.",
        ],
      },
    ],
  },

  {
    id: "campaign-builder",
    title: "Campaign Builder",
    icon: Sparkles,
    intro: "Turn a one-line brief into a ready-to-launch campaign — copy, images, video slideshow, and optional 24-variant matrix launch.",
    features: [
      {
        id: "brief",
        title: "AI Brief (Claude 4.5)",
        why: "Writes 4 hook variants, 3 ad-copy options, 3 CTAs, 3 descriptions, and 6 angle ideas in one pass. Saves the 45-min 'first draft' portion of every launch.",
        how: [
          "Enter title · vertical · audience · 1-line description.",
          "Click Generate brief.",
          "Edit inline if needed, then save — the brief is reusable across images, video, and matrix launch.",
        ],
      },
      {
        id: "images",
        title: "Image generation (Gemini Nano Banana)",
        why: "Produces 3-6 ad-ready creatives from the brief. Biased toward your winning DNA if Creative DNA is trained.",
        how: [
          "Click Generate images.",
          "Regenerate any single image with a tweak prompt.",
          "Best images auto-flow into Push-to-FB.",
        ],
      },
      {
        id: "video",
        title: "Informative video slideshow (ffmpeg)",
        why: "60% of RSOC verticals show higher CTR on video than static. We render a 15-second MP4 from your images + hooks, no editor needed.",
        how: ["Click Generate video. Wait ~30 seconds. Preview → download → attach to ad."],
      },
      {
        id: "push-fb",
        title: "Push to Facebook (paused)",
        why: "Creates campaign + adset + ad with your brief + images, saved paused so you review before spending a dollar.",
        how: ["Select ad account + page + landing URL → Push. Campaign appears in Ads Manager ready to activate."],
      },
      {
        id: "matrix",
        title: "Matrix Launch (24 variants @ $30)",
        why: "One brief × 4 angles × 3 audiences × 2 styles = 24 micro-tests for $1.25/variant. Meta's algorithm learns 10× faster than with single ads.",
        how: ["After generating a brief, click Matrix launch. Confirm budget. Fb pushes all 24 paused — activate whichever subset you want."],
      },
    ],
  },

  {
    id: "insights",
    title: "AI Insights",
    icon: LineChart,
    intro: "Claude reads yesterday's numbers and writes plain-English recommendations: who's winning, who to pause, which verticals to scale.",
    features: [
      {
        id: "summary",
        title: "Daily insights summary",
        why: "You shouldn't need to squint at a spreadsheet to know what happened. The summary tells you in 3 sentences.",
        how: ["Open /insights. Read the top card. Action items are in the Winners / Losers / Heatmap below."],
      },
      {
        id: "heatmap",
        title: "Vertical heatmap (Scale / Watch / Kill)",
        why: "Three-label verdict per vertical strips ambiguity from budget decisions.",
        how: ["Move budget from Kill verticals into Scale verticals — that's the play."],
      },
    ],
  },

  {
    id: "rules",
    title: "Auto-Rules",
    icon: Zap,
    intro: "If-this-then-that for ad accounts. Pause losers, raise budgets on winners, all running 24/7 in the background.",
    features: [
      {
        id: "rule-basics",
        title: "Build a rule",
        why: "Humans sleep. The market doesn't. Rules keep the good ads running and kill the bad ones while you're offline.",
        how: [
          "/rules → New rule.",
          "Pick trigger (ROI < -10%, spend > $X, CPA > $Y).",
          "Pick action (pause / raise budget / lower budget).",
          "Click Simulate to preview affected campaigns BEFORE activating.",
          "Save + enable.",
        ],
      },
      {
        id: "simulate",
        title: "Simulate preview",
        why: "Zero-risk dry-run — tells you exactly which campaigns the rule would touch, estimated budget delta, and sample per-campaign impact. Build trust before you flip the switch.",
        how: ["On any rule, click Simulate. Read the per-campaign table. Tweak thresholds if too aggressive."],
      },
    ],
  },

  {
    id: "optimize",
    title: "Optimize — 6 Operator Tactics",
    icon: Rocket,
    intro: "The meat of the app. Six high-leverage tactics that used to be tribal knowledge now in one page. Most important is the Auto-Operator banner at the top — arm it once and automation runs for you.",
    features: [
      {
        id: "auto-op",
        title: "Auto-Operator (ARMED/OFF banner)",
        icon: Zap,
        why: "One switch turns on fatigue rotation + winner scaling every 30 min, debounced 24h. Wake up to an already-optimized account.",
        how: ["Top of /optimize → Configure → enable + set thresholds → Save. Banner turns blue ARMED. 'Run now' for instant trigger."],
      },
      {
        id: "matrix-opt",
        title: "Matrix Launch tab",
        icon: Grid3x3,
        why: "Same as Campaign Builder's inline Matrix — accessible here for re-launching past briefs.",
        how: ["Pick a saved brief → Preview plan → Launch."],
      },
      {
        id: "dayparting",
        title: "Day-parting optimizer",
        icon: Clock,
        why: "You're burning 35% of night-time spend on dead hours. This finds them and schedules ads ON only in +40% ROI windows.",
        how: ["Open tab → review the 24-hour ROI curve → click Apply hot-window schedule. Ad sets are scheduled to run only during green hours."],
      },
      {
        id: "fatigue",
        title: "Creative fatigue rotator",
        icon: Flame,
        why: "CTR decays ~40% after day 7 on most verticals. Rotating to an A/B twin resets the audience signal and keeps CTR high.",
        how: ["Tab shows ads whose CTR has decayed ≥35% vs peak. Click Rotate A/B to swap. Or arm Auto-Operator to do this nightly."],
      },
      {
        id: "winners",
        title: "Winner-Scaling Protocol",
        icon: Rocket,
        why: "Proven formula: if a campaign holds +50% ROI at $100+/day for 48h, duplicate 3× with different age brackets at 2× budget. Double every 48h until ROI drops below 20%. Compounds aggressively.",
        how: ["Tab lists qualifying winners. Click Scale this winner → 3 duplicates queue. History tab shows every scale event."],
      },
      {
        id: "exclusions",
        title: "Exclusion Audiences",
        icon: UserMinus,
        why: "You pay per click — every single one. Every day Meta keeps re-showing ads to people who already clicked once and bounced. This excludes them from your targeting. Expect 15-30% cheaper CPA within a week.",
        how: [
          "Open Exclusions tab. Review the estimated monthly waste number at the top.",
          "Pick your ad account + FB Page → click Build clickers-minus-converters bundle.",
          "Select the bundle → paste your active adset IDs → Attach exclusion. Done.",
        ],
      },
      {
        id: "circuit",
        title: "Budget Circuit Breaker",
        icon: ShieldAlert,
        why: "Safety net. If spend exceeds your limit in a short window AND ROI is under your floor, the whole account is paused. Protects against runaway test-launches or broken tracking.",
        how: ["Circuit tab → admin sets spend limit + hours + ROI floor → Enable. Runs every 30 min with 1-hour debounce. Manual Pause-all button available."],
      },
    ],
  },

  {
    id: "intelligence",
    title: "Intelligence — 5 Self-Learning Tactics",
    icon: Dna,
    intro: "The app learns your account's patterns and feeds them back into image generation, keyword selection, and trend discovery.",
    features: [
      {
        id: "dna",
        title: "Creative DNA tagging",
        icon: Dna,
        why: "Claude extracts a 6-axis DNA (palette, subject, emotion, composition, text_style, setting) from every winning brief. Future image generation inherits those traits — you're compounding what works instead of rolling the dice on every new creative.",
        how: [
          "Open Creative DNA tab. The leaderboard shows tag → avg ROI.",
          "Click Extract DNA on each past brief to train the model.",
          "Test → paste any prompt → Enhance with winning DNA to see how it biases the output.",
        ],
      },
      {
        id: "keywords",
        title: "RSOC Keyword ordering",
        icon: Search,
        why: "System1 rewards keyword ordering. High-RPC terms should go first. We rank candidates by `RPC × √searches` so you don't burn tests on low-volume vanity terms.",
        how: ["Select a vertical → review the top-20 ranked keywords → copy the top 5 into your S1 forceKeys param."],
      },
    ],
  },

  {
    id: "scouting",
    title: "Trend Scouting",
    icon: Telescope,
    intro: "AI-driven vertical discovery from news and search signals. Daily feed of up-and-coming RSOC opportunities.",
    features: [
      {
        id: "scout",
        title: "Daily trend feed",
        why: "Being first to a trend is worth 3-5× the RPC vs arriving after saturation. This finds them for you.",
        how: ["Open /scouting. Bookmark any topic. Create a brief from it in one click."],
      },
    ],
  },

  {
    id: "digest",
    title: "Daily Digest email",
    icon: Mail,
    intro: "One email per morning summarizing yesterday + overnight automation + what needs your attention. Tap buttons inside the email to scale / pause without opening the app.",
    features: [
      {
        id: "digest-arm",
        title: "Set up the digest",
        why: "Your morning coffee becomes your stand-up. 30 seconds of reading replaces 15 minutes of dashboard squinting.",
        how: [
          "/digest → Enable + pick delivery hour + timezone.",
          "Save → Send test now to verify Resend key.",
          "Wake up tomorrow to the email.",
        ],
      },
      {
        id: "digest-action",
        title: "One-click Scale / Pause buttons",
        icon: MousePointerClick,
        why: "Read digest on the subway. Tap 'Scale 3×' next to the winner. Tap 'Pause now' on the loser. Account is optimized by the time you reach the office. Links are HMAC-signed, single-use, 48h expiry — safe to leave in the inbox.",
        how: ["The email has colored buttons next to each winner/loser/attention item. Tap → confirmation page opens → done."],
      },
    ],
  },

  {
    id: "capi",
    title: "FB CAPI Postback Bridge",
    icon: TrendingUp,
    intro: "The missing link that lets Facebook optimize against your real conversions instead of just clicks.",
    features: [
      {
        id: "capi",
        title: "How the bridge works",
        why: "RSOC runs on System1's pages which you don't control. Normally Meta never sees the conversion. This bridge catches S1 postbacks and forwards them as Purchase events to Meta's CAPI — dropping your CPA 20-40% within days.",
        how: [
          "Copy your personal postback URL from Team & Keys.",
          "Paste it into System1's 'server postback URL' field.",
          "Verify fbclid is in your landing URL params. Done — conversions start flowing.",
        ],
      },
    ],
  },

  {
    id: "team",
    title: "Team & Keys",
    icon: Users,
    intro: "Workspace-level credential management. Admin controls S1 + Resend + fal.ai + ElevenLabs; each buyer controls their own FB connection.",
    features: [
      {
        id: "invite",
        title: "Invite buyers / viewers",
        why: "Scale beyond you without sharing your personal FB token.",
        how: ["Team page → Invite → enter email + role → send. Invite link in email — accepted user sets their own password."],
      },
      {
        id: "keys",
        title: "Workspace keys",
        why: "Central config. One S1 key + one Resend key serves the entire team.",
        how: ["Admin pastes keys → Save. Team inherits automatically."],
      },
    ],
  },

  {
    id: "connections",
    title: "My Facebook",
    icon: Plug,
    intro: "Your personal FB connection — every buyer brings their own.",
    features: [
      {
        id: "connect",
        title: "Connect FB",
        why: "Each buyer's token is stored only on their account. No key-sharing, no accidental cross-account actions.",
        how: ["My Facebook → Paste access token → Save. Ad accounts load automatically."],
      },
    ],
  },
];

export default function Docs() {
  const [open, setOpen] = useState("getting-started");

  return (
    <div className="p-6 max-w-[1500px]" data-testid="docs-page" style={{ fontFamily: "Manrope" }}>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Documentation</h1>
          <p className="text-sm text-slate-500 mt-1">
            What each feature does · why it matters · how to use it. Bookmark this.
          </p>
        </div>
        <button
          onClick={restartTour}
          data-testid="replay-tour-btn"
          className="shrink-0 flex items-center gap-2 bg-white border border-slate-200 hover:border-blue-400 rounded-md px-3 py-2 text-xs font-medium text-slate-700 hover:text-blue-700 transition-colors"
        >
          <Play className="w-3.5 h-3.5" /> Replay product tour
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px,1fr] gap-6">
        {/* Sidebar nav */}
        <aside className="lg:sticky lg:top-4 self-start">
          <div className="bg-white border border-slate-200 rounded-md p-1.5" data-testid="docs-nav">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const active = open === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => {
                    setOpen(s.id);
                    document.getElementById(`docs-${s.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  data-testid={`docs-nav-${s.id}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded text-xs text-left transition-colors ${
                    active ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {s.title}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Sections */}
        <main className="space-y-8">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <section key={s.id} id={`docs-${s.id}`} className="scroll-mt-4" data-testid={`docs-section-${s.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-9 h-9 rounded-md bg-blue-600 text-white flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h2 className="text-xl font-semibold tracking-tight">{s.title}</h2>
                </div>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">{s.intro}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {s.features.map((f) => {
                    const FIcon = f.icon || Icon;
                    return (
                      <div key={f.id} data-testid={`docs-feat-${f.id}`} className="bg-white border border-slate-200 rounded-md p-5 hover:border-blue-300 transition-colors">
                        <div className="flex items-center gap-2 mb-2">
                          <FIcon className="w-4 h-4 text-blue-600" />
                          <h3 className="text-sm font-semibold text-slate-900">{f.title}</h3>
                        </div>
                        <div className="mb-3">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Why it matters</div>
                          <p className="text-[13px] text-slate-700 leading-relaxed">{f.why}</p>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">How to use it</div>
                          <ol className="text-[13px] text-slate-700 leading-relaxed space-y-1 list-decimal pl-4">
                            {f.how.map((step, i) => <li key={`${f.id}-${i}`}>{step}</li>)}
                          </ol>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-md p-5 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-slate-900 leading-relaxed">
              <b>Something missing?</b> Ask your admin to drop it in the team Slack — we ship most requests within a week.
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
